import { CSSProperties, useMemo } from "react";
import sanityImageUrl from "@sanity/image-url";
import { useSanityClient } from "../useSanityClient";
import { isPresent } from "../utils/checks";
import {
  toSanityAsset,
  buildSrc,
  buildSrcSet,
  parseSanityImageRef
} from "./utils";
import { SanityImageType } from "./types";

interface UseSanityImageResponse {
  width?: number | string;
  height?: number | string;
  srcSet: string;
  src: string;
  imageStyle?: CSSProperties;
  format: string;
}

interface UseSanityImageProps extends SanityImageType {
  width?: number | string;
  height?: number | string;
  fit?: string;
}

export const useSanityImage = (
  imageProps: UseSanityImageProps
): UseSanityImageResponse | undefined => {
  const { hotspot, width, height, fit } = imageProps;

  const sanityClient = useSanityClient({ useCdn: true });
  const builder = sanityImageUrl(sanityClient);

  const style = useMemo<CSSProperties | undefined>(() => {
    if (!hotspot) return undefined;

    return {
      objectPosition: [hotspot.x, hotspot.y]
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map((value) => (value! * 100).toFixed(2) + "%")
        .join(" ")
    };
  }, [hotspot]);

  const sanityAsset = toSanityAsset(imageProps);
  if (!isPresent(sanityAsset)) return undefined;
  const format = parseSanityImageRef(sanityAsset._id).format;

  // Create default src and build srcSet
  const src = buildSrc(builder, sanityAsset, { width, height, fit });
  const srcSet = buildSrcSet(builder, sanityAsset, {
    width,
    height,
    fit
  });

  //   if (options.__experimentalAspectRatio && isPresent(crop)) {
  //     // If enabled, this will estimate the final aspect ratio based on
  //     // the dimensions of the original image and the crop parameter,
  //     // then use this aspect ratio to apply `width` and `height` attrs
  //     // to both the preview and final images.
  //     //
  //     // Note: No attempts are made to compensate for the `fit` mode or
  //     // image params that transform the final output dimensions in this
  //     // early proof-of-concept version.
  //     const { dimensions } = parseSanityImageRef(sanityAsset._id);

  //     // If `crop` isn't set, use fallback values.
  //     const { left = 0, right = 0, top = 0, bottom = 0 } = crop || {};
  //     const croppedWidth = dimensions.width * (1 - left - right);
  //     const croppedHeight = dimensions.height * (1 - top - bottom);
  //     const ratio = croppedWidth / croppedHeight;

  //     width = width ? parseInt(width?.toString(), 10) : dimensions.width;
  //     height = height || Math.round(width / ratio);
  //   }

  return {
    imageStyle: style,
    width,
    height,
    srcSet,
    src,
    format
  };
};
